import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Lock, CheckCircle2, AlertCircle, Clock3 } from "lucide-react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { fetchPublicSubmission } from "../service/ProfileService";

function statusClass(status) {
    if (!status) return "badge-processing";
    const s = status.toLowerCase();
    if (s === "completed") return "badge-complete";
    if (s === "error") return "badge-error";
    return "badge-processing";
}

function StatusIcon({ status }) {
    if (!status) return <Clock3 size={12} />;
    const s = status.toLowerCase();
    if (s === "completed") return <CheckCircle2 size={12} />;
    if (s === "error") return <AlertCircle size={12} />;
    return <Clock3 size={12} />;
}

function statusLabel(status) {
    if (!status) return "Processing";
    const s = status.toLowerCase();
    if (s === "completed") return "Complete";
    if (s === "error") return "Error";
    return "Processing";
}

function overall(m) {
    if (!m || m.noveltyScore == null || m.methodScore == null || m.impactScore == null) return null;
    return Math.round((m.noveltyScore + m.methodScore + m.impactScore) / 3);
}

export default function PublicSubmission() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const userId = params.get("user");
    const manuscriptId = params.get("id");

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId || !manuscriptId) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        fetchPublicSubmission(userId, manuscriptId)
            .then(res => { if (!cancelled) setData(res); })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
    }, [userId, manuscriptId]);

    const renderBody = () => {
        if (loading) return <div className="profile-state">Loading submission…</div>;
        if (!data) return <div className="profile-state">We couldn't load that submission.</div>;
        if (!data.isPublic) {
            return (
                <div className="profile-private">
                    <div className="profile-private-icon"><Lock size={28} /></div>
                    <h2 className="profile-private-title">This submission isn't viewable</h2>
                    <p className="profile-private-sub">The researcher's profile is private, so their submissions aren't shared.</p>
                </div>
            );
        }
        if (data.notFound || !data.manuscript) {
            return <div className="profile-state">This submission no longer exists.</div>;
        }

        const m = data.manuscript;
        const owner = data.owner;
        const score = overall(m);
        const scoredDate = m.scoredDate ? new Date(m.scoredDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : null;

        return (
            <>
                <section className="pub-sub-hero">
                    <div className="pub-sub-meta">
                        <span className={`status-badge ${statusClass(m.status)}`}>
                            <StatusIcon status={m.status} />
                            <span className="status-label-full">{statusLabel(m.status)}</span>
                        </span>
                        {scoredDate && <span className="pub-sub-date">Scored {scoredDate}</span>}
                    </div>
                    <h1 className="pub-sub-title">{m.title || "Untitled manuscript"}</h1>
                    {m.authors && <p className="pub-sub-authors">{m.authors}</p>}
                    {owner && (
                        <p className="pub-sub-owner">
                            By <button className="pub-sub-owner-link" onClick={() => navigate(`/profile?id=${owner.id}`)}>{owner.displayName}</button>
                        </p>
                    )}
                </section>

                <section className="pub-sub-scores">
                    <div className="pub-sub-score-card">
                        <p className="pub-sub-score-label">Novelty</p>
                        <p className="pub-sub-score-value" style={{ color: "var(--accent-text)" }}>{m.noveltyScore ?? "—"}</p>
                    </div>
                    <div className="pub-sub-score-card">
                        <p className="pub-sub-score-label">Methodology</p>
                        <p className="pub-sub-score-value" style={{ color: "var(--series-blue)" }}>{m.methodScore ?? "—"}</p>
                    </div>
                    <div className="pub-sub-score-card">
                        <p className="pub-sub-score-label">Impact</p>
                        <p className="pub-sub-score-value" style={{ color: "var(--bronze)" }}>{m.impactScore ?? "—"}</p>
                    </div>
                    <div className="pub-sub-score-card pub-sub-score-overall">
                        <p className="pub-sub-score-label">Overall</p>
                        <p className="pub-sub-score-value">{score ?? "—"}</p>
                    </div>
                </section>

                {(m.novelty || m.method || m.impact) && (
                    <section className="pub-sub-analysis">
                        {m.novelty && (
                            <div className="pub-sub-analysis-card">
                                <p className="pub-sub-analysis-title" style={{ color: "var(--accent-text)" }}>Novelty Analysis</p>
                                <p className="pub-sub-analysis-text">{m.novelty}</p>
                            </div>
                        )}
                        {m.method && (
                            <div className="pub-sub-analysis-card">
                                <p className="pub-sub-analysis-title" style={{ color: "var(--series-blue)" }}>Methodology Analysis</p>
                                <p className="pub-sub-analysis-text">{m.method}</p>
                            </div>
                        )}
                        {m.impact && (
                            <div className="pub-sub-analysis-card">
                                <p className="pub-sub-analysis-title" style={{ color: "var(--bronze)" }}>Impact Analysis</p>
                                <p className="pub-sub-analysis-text">{m.impact}</p>
                            </div>
                        )}
                    </section>
                )}
            </>
        );
    };

    return (
        <div className="home-layout">
            <Sidebar activePage="" />
            <main className="home-main">
                <Header />
                <div className="profile-page">
                    <button className="profile-back" onClick={() => navigate(-1)}>
                        <ChevronLeft size={15} />
                        Back
                    </button>
                    {renderBody()}
                </div>
            </main>
        </div>
    );
}
